"use client";

import { Carousel, CarouselApi, CarouselContent, CarouselItem, cn, useIsMobile, useIsTablet } from "@saridana-core/react-ui-kit";
import { TTestimonialSection, TTestimonyCard } from "@shared/types";
import { SectionHeading, TestimonyCard, TestimonyDialog } from "@shared/ui";
import { useEffect, useState } from "react";

export function TestimonialSection({ title, description, testimonies, secondsToChange }: TTestimonialSection) {
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [current, setCurrent] = useState(0);
  const [selectedTestimony, setSelectedTestimony] = useState<TTestimonyCard | null>(null);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const getMappedTestimonies = () => {
    if (testimonies && testimonies.length > 0) {
      if (testimonies.length === 2) {
        return [...testimonies];
      }

      return !isMobile ? [...testimonies, ...testimonies.slice(0, isTablet ? 1 : 2)] : [...testimonies];
    }

    return [];
  };
  const mappedTestimonies = getMappedTestimonies();

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
      setSelectedTestimony(mappedTestimonies[api.selectedScrollSnap()]);
    };
    api.on("select", onSelect);
    onSelect();

    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  const delay = secondsToChange * 1000;

  const handleSelect = (testimony: TTestimonyCard) => {
    if (api) {
      const index = testimonies.findIndex((item) => item.id === testimony.id);

      if (index !== -1) {
        setCurrent(index);
        setSelectedTestimony(mappedTestimonies[index]);
        api.scrollTo(index);
      }
    }
  };

  return (
    <section className="py-20 sm:py-[100px] lg:py-[120px] relative overflow-hidden bg-white">
      <div className="container relative">
        <SectionHeading className="text-center" title={title} description={description} descriptionClassName="mx-auto" />

        {mappedTestimonies?.length > 0 && (
          <div className="mt-14 md:mt-[72px]">
            <Carousel
              opts={{ align: "start", loop: true }}
              setApi={setApi}
              className="w-full"
              plugins={[{ plugin: "autoplay", options: { active: !!secondsToChange && !isDialogOpen, delay } }]}
            >
              <CarouselContent className="md:max-h-[560px]">
                {mappedTestimonies.map((testimony, index) => (
                  <CarouselItem
                    key={"testimony-item-" + testimony.id + "-" + index}
                    className={cn(
                      current === index
                        ? "basis-[100%] md:basis-[80%] xl:basis-[50%]"
                        : mappedTestimonies.length === 2
                          ? "basis-[100%] md:basis-[40%] cursor-pointer xl:basis-[50%]"
                          : "basis-[100%] md:basis-[40%] cursor-pointer xl:basis-[25%]",
                    )}
                    onClick={() => handleSelect(testimony)}
                  >
                    <TestimonyCard
                      {...testimony}
                      testimonies={testimonies}
                      isActive={current === index}
                      onClick={() => setIsDialogOpen(true)}
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
        )}

        {mappedTestimonies && mappedTestimonies.length > 0 && (
          <div
            className={cn("mt-10 flex items-center justify-center text-center gap-2.5", mappedTestimonies.length === 2 ? "xl:hidden" : "")}
          >
            {mappedTestimonies.map((testimony, index) => {
              return (
                <a
                  key={"article-slide-indicator-" + index}
                  className={cn(
                    "inline-block rounded-full size-[14px] cursor-pointer",
                    index === current ? "bg-primary-500" : "bg-gray-200",
                  )}
                  onClick={() => handleSelect(testimony)}
                >
                  &nbsp;
                </a>
              );
            })}
          </div>
        )}
      </div>
      <TestimonyDialog
        isOpen={isDialogOpen}
        selected={selectedTestimony}
        testimonies={testimonies}
        onOpenChange={(open) => setIsDialogOpen(open)}
        onSelect={handleSelect}
      />
    </section>
  );
}
